import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingCart, Globe, User, ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useQuote } from '../contexts/QuoteContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_5a86fca4-b52a-477d-a2ba-6ba3a9509c91/artifacts/dxnk9k47_The%20Guy%20In%20SA%20Main%20Logo.png';

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, isAuthenticated, logout, isAdmin, isSupplier } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const { getItemCount } = useQuote();
  const location = useLocation();

  const isActive = (path) => location.pathname === path;
  const itemCount = getItemCount();

  const navLinks = [
    { href: '/', label: t('nav_home') },
    { href: '/services', label: t('nav_services') },
    { href: '/products', label: t('nav_products') },
    { href: '/for-suppliers', label: t('nav_for_suppliers') },
    { href: '/for-buyers', label: t('nav_for_buyers') },
    { href: '/contact', label: t('nav_contact') },
  ];

  const getDashboardLink = () => {
    if (isAdmin) return '/admin';
    if (isSupplier) return '/supplier';
    return '/buyer';
  };

  return (
    <nav className="glass-nav sticky top-0 z-50" data-testid="navbar">
      <div className="container-custom">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="The Guy in SA" 
              className="h-10 lg:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive(link.href)
                    ? 'text-[#1E3A8A] bg-[#1E3A8A]/10'
                    : 'text-slate-600 hover:text-[#1E3A8A] hover:bg-slate-100'
                }`}
                data-testid={`nav-link-${link.href.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-2 lg:gap-4">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1 px-2 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100 transition-colors"
              data-testid="language-toggle"
            >
              <Globe className="w-4 h-4" />
              <span className="hidden sm:inline">{language.toUpperCase()}</span>
            </button>

            {/* Cart */}
            <Link
              to="/cart"
              className="relative p-2 rounded-md text-slate-600 hover:bg-slate-100 transition-colors"
              data-testid="quote-cart-link"
            >
              <ShoppingCart className="w-5 h-5" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#F59E0B] text-slate-900 text-xs font-bold rounded-full flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Link>

            {/* Auth */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                    data-testid="user-menu-trigger"
                  >
                    <div className="w-8 h-8 rounded-full bg-[#1E3A8A] text-white flex items-center justify-center text-sm font-semibold">
                      {user?.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <span className="hidden md:inline max-w-[100px] truncate">{user?.name}</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to={getDashboardLink()} className="cursor-pointer" data-testid="dashboard-link">
                      {t('nav_dashboard')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={logout} 
                    className="cursor-pointer text-red-600"
                    data-testid="logout-button"
                  >
                    {t('nav_logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" size="sm" data-testid="login-button">
                    {t('nav_login')}
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    size="sm" 
                    className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90"
                    data-testid="register-button"
                  >
                    {t('nav_register')}
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <button
              className="lg:hidden p-2 rounded-md text-slate-600 hover:bg-slate-100"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-slate-200" data-testid="mobile-menu">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                    isActive(link.href)
                      ? 'text-[#1E3A8A] bg-[#1E3A8A]/10'
                      : 'text-slate-600 hover:text-[#1E3A8A] hover:bg-slate-100'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              {!isAuthenticated && (
                <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-slate-200">
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full">
                      {t('nav_login')}
                    </Button>
                  </Link>
                  <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full bg-[#1E3A8A] hover:bg-[#1E3A8A]/90">
                      {t('nav_register')}
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
